import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
} from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import config from "../../config/config";
import "./testimonials.scss";

function EditTestimonials() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    title: "",
    tag: "",
    person_name: "",
    designation: "",
    message: "",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const breadcrumbItems = [
    { title: "Testimonials", link: "/testimonials" },
    { title: "Edit Testimonial", link: "#" },
  ];

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http") || photo.startsWith("data:")) return photo;
    const baseUrl = config.API_BASE_URL.replace(/\/api\/?$/, "");
    return photo.startsWith("/") ? `${baseUrl}${photo}` : `${baseUrl}/uploads/${photo}`;
  };

  useEffect(() => {
    if (location.state && location.state.testimonial) {
      const item = location.state.testimonial;
      setFormData({
        title: item.title || "",
        tag: item.tag || "",
        person_name: item.person_name || item.personName || item.name || "",
        designation: item.designation || "",
        message: item.message || "",
      });
      if (item.photo) {
        setPhotoPreview(getPhotoUrl(item.photo));
      }
    } else if (id) {
      fetchTestimonialDetails(id);
    }
  }, [id, location.state]);

  const fetchTestimonialDetails = async (testimonialId) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/testimonials/${testimonialId}`, {
        headers: { "ngrok-skip-browser-warning": "true" },
      });
      if (response.ok) {
        const item = await response.json();
        const detail = item.testimonial || item;
        setFormData({
          title: detail.title || "",
          tag: detail.tag || "",
          person_name: detail.person_name || detail.personName || detail.name || "",
          designation: detail.designation || "",
          message: detail.message || "",
        });
        if (detail.photo) {
          setPhotoPreview(getPhotoUrl(detail.photo));
        }
      }
    } catch (err) {
      console.error("Error fetching testimonial details:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      if (formData.tag) formDataToSend.append("tag", formData.tag);
      formDataToSend.append("person_name", formData.person_name);
      if (formData.designation) formDataToSend.append("designation", formData.designation);
      formDataToSend.append("message", formData.message);
      if (photoFile) {
        formDataToSend.append("photo", photoFile);
      }

      const response = await fetch(`${config.API_BASE_URL}/testimonials/${id}`, {
        method: "PUT",
        headers: {
          "ngrok-skip-browser-warning": "true",
        },
        body: formDataToSend,
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        navigate("/testimonials");
      } else {
        alert(data.message || data.error || "Failed to update testimonial");
      }
    } catch (error) {
      console.error("Error updating testimonial:", error);
      alert("Error updating testimonial");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/testimonials");
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Edit Testimonial"
            breadcrumbItems={breadcrumbItems}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Title <span className="text-danger">*</span></Label>
                          <Input
                            type="text"
                            name="title"
                            className="form-control"
                            placeholder="Enter Title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Tag</Label>
                          <Input
                            type="text"
                            name="tag"
                            className="form-control"
                            placeholder="Enter Tag"
                            value={formData.tag}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Person Name <span className="text-danger">*</span></Label>
                          <Input
                            type="text"
                            name="person_name"
                            className="form-control"
                            placeholder="Enter Person Name"
                            value={formData.person_name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Designation</Label>
                          <Input
                            type="text"
                            name="designation"
                            className="form-control"
                            placeholder="Enter Designation"
                            value={formData.designation}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <div className="mb-3">
                          <Label className="form-label">Message <span className="text-danger">*</span></Label>
                          <Input
                            type="textarea"
                            name="message"
                            rows="4"
                            className="form-control"
                            placeholder="Enter testimonial message..."
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label">Photo</Label>
                          <Input
                            type="file"
                            className="form-control"
                            onChange={handlePhotoChange}
                            accept="image/*"
                          />
                          {photoPreview && (
                            <div className="mt-2">
                              <img
                                src={photoPreview}
                                alt="Photo Preview"
                                className="img-thumbnail"
                                style={{ maxHeight: "120px" }}
                              />
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col className="d-flex justify-content-end">
                        <Button
                          type="submit"
                          color="primary"
                          className="me-2"
                          disabled={submitting}
                        >
                          {submitting ? "Updating..." : "Update Testimonial"}
                        </Button>
                        <Button
                          type="button"
                          color="secondary"
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default EditTestimonials;
